import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ICON_SM, ScrumPhaseIcon } from '../components/icons/PortfolioIcons';

const ScrumSection = styled.section`
  margin-top: 0.5rem;
  padding: 3.25rem 2rem;
  border-radius: 16px;
  background: linear-gradient(
    180deg,
    #080000 0%,
    ${({ theme }) => theme.colors.dark} 100%
  );
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
  }
`;

const ScrumHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.85rem;
  max-width: 860px;
  margin: 0 auto 2rem;
`;

const ScrumTitle = styled.h2`
  margin: 0;
  font-size: 3rem;
  line-height: 1.1;
  color: #ffffff;

  span {
    background: linear-gradient(123deg, #be1515, #ffc800);
    background-size: 220% 220%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    animation: titleGradientShift 14s ease-in-out infinite;
  }

  @keyframes titleGradientShift {
    0% {
      background-position: 0% 50%;
    }
    35% {
      background-position: 100% 50%;
    }
    70% {
      background-position: 30% 100%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @media (max-width: 768px) {
    font-size: 2.1rem;
  }
`;

const ScrumSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
  font-size: 1.2rem;
  max-width: 760px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PhasesRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 2.2rem;
`;

const PhaseChip = styled.button<{ $active: boolean }>`
  --scrum-purple:rgb(255, 255, 255);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${({ $active }) =>
    $active ? 'rgba(255, 255, 255, 0.24)' : 'rgba(255, 255, 255, 0.03)'};
  color: ${({ $active }) => ($active ? '#ffffff' : 'rgba(255, 255, 255, 0.65)')};
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.42rem 0.9rem;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    color: #ffffff;
  }

  svg {
    flex-shrink: 0;
    opacity: ${({ $active }) => ($active ? 1 : 0.75)};
  }
`;

const ScrumMain = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.8rem;
  align-items: start;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const LeftCardWrap = styled.div`
  position: relative;
  min-height: 350px;
`;

const LeftPhaseCard = styled(motion.article)<{ $gradient: string }>`
  border-radius: 34px;
  padding: 2rem;
  background: ${({ $gradient }) => $gradient};
  min-height: 350px;
  color: #ffffff;
  position: absolute;
  inset: 0;
  overflow: hidden;
  box-shadow: 0 18px 40px rgba(179, 21, 21, 0.28);

  &::before {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 140px;
    height: 140px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
    animation: orbDriftPulseOne 16s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    left: -32px;
    bottom: -32px;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    animation: orbDriftPulseTwo 18s ease-in-out infinite;
  }

  @keyframes orbDriftPulseOne {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    33% {
      transform: translate(-8px, 6px) scale(1.08);
    }
    66% {
      transform: translate(6px, -7px) scale(0.94);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }

  @keyframes orbDriftPulseTwo {
    0% {
      transform: translate(0px, 0px) scale(1);
    }
    35% {
      transform: translate(7px, -6px) scale(0.92);
    }
    70% {
      transform: translate(-6px, 8px) scale(1.1);
    }
    100% {
      transform: translate(0px, 0px) scale(1);
    }
  }
`;

const PhaseBadge = styled.span`
  display: inline-flex;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  padding: 0.35rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 1.1rem;
`;

const PhaseIcon = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PhaseTitle = styled.h3`
  margin: 0;
  font-size: 2.35rem;
  line-height: 1.15;
  margin-bottom: 0.7rem;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const PhaseSub = styled.p`
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.05rem;
  font-weight: 500;
`;

const PhaseDesc = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 1.05rem;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 350px;
`;

const FeaturesTitle = styled.h4`
  margin: 0 0 0.95rem;
  color: #ffffff;
  font-size: 1.1rem;
  letter-spacing: 0.08em;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  flex: 1;
`;

const FeatureCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  padding: 0.85rem 1rem;
`;

const FeatureIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  border-radius: 999px;
  background: rgba(250, 139, 139, 0.28);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
`;

const FeatureText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
`;

const Controls = styled.div`
  margin-top: 1.15rem;
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

const CircleBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.88);
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    border-color: rgba(255, 255, 255, 0.7);
    color: #ffffff;
  }

  svg {
    display: block;
  }
`;

const ProgressWrap = styled.div`
  flex: 1;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
`;

const ProgressBar = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  border-radius: 999px;
  background:rgb(255, 255, 255);
  transition: width 0.35s ease;
`;

const ProgressLabel = styled.p`
  margin: 0.4rem 0 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  text-align: center;
`;

const PauseButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.24);
  color: #ffffff;
  border-radius: 999px;
  height: 40px;
  padding: 0 0.9rem;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.36);
    color: #ffffff;
  }
`;

type ScrumPhase = {
  phase: string;
  title: string;
  subtitle: string;
  description: string;
  aiFeatures: string[];
  gradient: string;
};

type ScrumPhaseLocalized = Omit<ScrumPhase, 'gradient'>;

const scrumPhaseGradients: string[] = [
  'linear-gradient(135deg, #170303 0%, #400707 100%)',
  'linear-gradient(135deg, #400707 0%, #170303 100%)',
  'linear-gradient(135deg, #170303 0%, #520000 100%)',
  'linear-gradient(135deg, #400707 0%, #690c0c 100%)',
  'linear-gradient(135deg, #170303 0%, #7d0201 100%)',
  'linear-gradient(135deg, #400707 0%, #921111 100%)',
  'linear-gradient(135deg, #170303 0%, #b31515 100%)',
  'linear-gradient(135deg, #520000 0%, #e41b1b 100%)',
];

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [activePhase, setActivePhase] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAutoPlay || isInteracting) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setActivePhase((prev) => (prev === scrumPhaseGradients.length - 1 ? 0 : prev + 1));
    }, 7000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAutoPlay, isInteracting]);

  const goToPhase = (index: number) => {
    setActivePhase(index);
  };

  const prevPhase = () => {
    setActivePhase((prev) => (prev === 0 ? scrumPhaseGradients.length - 1 : prev - 1));
  };

  const nextPhase = () => {
    setActivePhase((prev) => (prev === scrumPhaseGradients.length - 1 ? 0 : prev + 1));
  };

  const localizedPhases = t('home.scrumSection.phases', {
    returnObjects: true,
  }) as ScrumPhaseLocalized[];

  const scrumPhases: ScrumPhase[] = scrumPhaseGradients.map((gradient, index) => {
    const localized = localizedPhases?.[index];
    return {
      gradient,
      phase: localized?.phase ?? '',
      title: localized?.title ?? '',
      subtitle: localized?.subtitle ?? '',
      description: localized?.description ?? '',
      aiFeatures: localized?.aiFeatures ?? [],
    };
  });

  const currentPhase = scrumPhases[activePhase] ?? scrumPhases[0];
  
  return (
    <>
      <ScrumSection
        onMouseEnter={() => setIsInteracting(true)}
        onMouseLeave={() => setIsInteracting(false)}
        onFocusCapture={() => setIsInteracting(true)}
        onBlurCapture={() => setIsInteracting(false)}
      >
        <ScrumHeader>
          <ScrumTitle>
            {t('home.scrumSection.titlePrefix')} <span>{t('home.scrumSection.titleHighlight')}</span>
          </ScrumTitle>
          <ScrumSubtitle>
            {t('home.scrumSection.subtitle')}
          </ScrumSubtitle>
        </ScrumHeader>

        <PhasesRow>
          {scrumPhases.map((phase, index) => (
            <PhaseChip
              key={phase.phase}
              type="button"
              $active={index === activePhase}
              onClick={() => goToPhase(index)}
            >
              <ScrumPhaseIcon index={index} size={ICON_SM} color="currentColor" />
              {phase.phase}
            </PhaseChip>
          ))}
        </PhasesRow>

        <ScrumMain>
          <LeftCardWrap>
            <AnimatePresence mode="wait">
              <LeftPhaseCard
                key={currentPhase.phase}
                $gradient={currentPhase.gradient}
                initial={{ opacity: 0, x: -16, scale: 0.98 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: 16, scale: 0.98 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <PhaseBadge>{currentPhase.phase}</PhaseBadge>
                <PhaseIcon>
                  <ScrumPhaseIcon index={activePhase} />
                </PhaseIcon>
                <PhaseTitle>{currentPhase.title}</PhaseTitle>
                <PhaseSub>{currentPhase.subtitle}</PhaseSub>
                <PhaseDesc>{currentPhase.description}</PhaseDesc>
              </LeftPhaseCard>
            </AnimatePresence>
          </LeftCardWrap>

          <RightPanel>
            <FeaturesTitle>{t('home.scrumSection.featuresTitle')}</FeaturesTitle>
            <AnimatePresence mode="wait">
              <FeatureList key={`${currentPhase.phase}-features`}>
                {currentPhase.aiFeatures.map((feature, index) => (
                  <FeatureCard
                    key={`${currentPhase.phase}-${feature}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25, delay: index * 0.04 }}
                  >
                    <FeatureIndex>{String(index + 1).padStart(2, '0')}</FeatureIndex>
                    <FeatureText>{feature}</FeatureText>
                  </FeatureCard>
                ))}
              </FeatureList>
            </AnimatePresence>

            <Controls>
              <CircleBtn type="button" onClick={prevPhase} aria-label={t('home.scrumSection.controls.prevAria')}>
                <ChevronLeft size={22} aria-hidden />
              </CircleBtn>

              <ProgressWrap>
                <ProgressTrack>
                  <ProgressBar $percent={((activePhase + 1) / scrumPhaseGradients.length) * 100} />
                </ProgressTrack>
                <ProgressLabel>
                  {activePhase + 1} / {scrumPhaseGradients.length}
                </ProgressLabel>
              </ProgressWrap>

              <PauseButton
                type="button"
                onClick={() => setIsAutoPlay((prev) => !prev)}
                aria-label={
                  isAutoPlay
                    ? t('home.scrumSection.controls.pauseAria')
                    : t('home.scrumSection.controls.resumeAria')
                }
              >
                {isAutoPlay ? t('home.scrumSection.controls.pause') : t('home.scrumSection.controls.resume')}
              </PauseButton>

              <CircleBtn type="button" onClick={nextPhase} aria-label={t('home.scrumSection.controls.nextAria')}>
                <ChevronRight size={22} aria-hidden />
              </CircleBtn>
            </Controls>
          </RightPanel>
        </ScrumMain>
      </ScrumSection>
    </>
  );
};

export default HomePage; 